'use client';

import { SearchIcon } from 'assets/icons';
import { useRouter } from 'next/navigation';
import { Fragment, useEffect, useRef, useState } from 'react';
import { searchFood } from 'lib/ogdb/search';
import type { FoodSummary } from 'lib/ogdb/types';
import { Container } from '..';
import { FoodListElement } from './FoodListElement';
import { SuggestiveChips } from './SuggestiveChips';
import { foodHref } from './types';
import styles from './SearchComponent.module.css';

const cx = (...args: Array<string | false | undefined | null>) =>
  args.filter(Boolean).join(' ');

const MIN_SUGGEST_LENGTH = 3;
const SUGGEST_DEBOUNCE_MS = 300;
const MAX_QUERY_LENGTH = 100;

export const SearchComponent = ({
  className,
  defaultSearchString,
}: {
  className?: string;
  defaultSearchString?: string;
}) => {
  const router = useRouter();
  const [search, setSearch] = useState<boolean>(false);
  const [searchString, setSearchString] = useState<string>(
    defaultSearchString ?? '',
  );
  const [edited, setEdited] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<FoodSummary[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Follow the URL's query (back/forward) instead of keeping the first one.
  const [seenDefault, setSeenDefault] = useState(defaultSearchString);
  if (seenDefault !== defaultSearchString) {
    setSeenDefault(defaultSearchString);
    setSearchString(defaultSearchString ?? '');
    setEdited(false);
  }

  const query = searchString.trim();

  useEffect(() => {
    if (!search || !edited || query.length < MIN_SUGGEST_LENGTH) {
      setSuggestions((prev) => (prev.length ? [] : prev));
      return;
    }
    let stale = false;
    const timer = setTimeout(() => {
      searchFood({ query, count: 4 }).then((res) => {
        if (!stale) setSuggestions(res);
      });
    }, SUGGEST_DEBOUNCE_MS);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [search, edited, query]);

  // Blur drops the mobile keyboard and un-focuses the now-inert closed box.
  const close = () => {
    setSearch(false);
    setEdited(false);
    setSearchString(defaultSearchString ?? '');
    inputRef.current?.blur();
  };

  const submit = (input: string) => {
    const q = input.trim();
    if (!q) return;
    setSearchString(q);
    setEdited(false);
    setSearch(false);
    inputRef.current?.blur();
    router.push(`/ogdb/search?q=${encodeURIComponent(q)}`);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(e.target.value);
    setEdited(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') close();
    // 229: Safari's composition-confirming Enter (isComposing is already false).
    else if (
      e.key === 'Enter' &&
      !e.nativeEvent.isComposing &&
      e.keyCode !== 229
    ) {
      submit(searchString);
    }
  };

  return (
    <div className={cx(styles.root, className)}>
      {search ? (
        <div
          className={styles.mask}
          onClick={close}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') close();
          }}
          role="button"
          aria-label="Close search"
          tabIndex={0}
        />
      ) : null}
      {!search ? (
        <div className={styles.searchFood}>
          <input
            ref={inputRef}
            type="text"
            placeholder="Search for food"
            aria-label="Search for food"
            maxLength={MAX_QUERY_LENGTH}
            enterKeyHint="search"
            onFocus={() => setSearch(true)}
            onClick={() => setSearch(true)}
            value={searchString}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
          />
          <SearchIcon className={styles.search} />
          <div className={styles.chipsContainer}>
            <SuggestiveChips
              onClick={submit}
              data={[
                { emoji: '🍕', title: 'Pizza' },
                { emoji: '🍔', title: 'Burger' },
                { emoji: '🍲', title: 'Poha' },
                { emoji: '🍦', title: 'Ice Cream' },
                { emoji: '💪🏻', title: 'Protein' },
                { emoji: '🫓', title: 'Dosa' },
                { emoji: '🌯', title: 'Roll' },
                { emoji: '🥘', title: 'Biryani' },
                { emoji: '🥗', title: 'Salad' },
              ]}
            />
          </div>
          <div className={styles.chipsContainer}>
            <SuggestiveChips
              onClick={submit}
              data={[
                { emoji: '🫓', title: 'Paratha' },
                { emoji: '🌯', title: 'Burrito' },
                { emoji: '🥖', title: 'Sub' },
                { emoji: '🥪', title: 'Sandwich' },
                { emoji: '🍳', title: 'Omlette' },
                { emoji: '🥛', title: 'Whey' },
                { emoji: '🍜', title: 'Noodle' },
                { emoji: '🫓', title: 'Idli' },
                { emoji: '🥗', title: 'Chicken Salad' },
              ]}
            />
          </div>
        </div>
      ) : (
        <div className={styles.searchFoodOverlay}>
          <input
            ref={inputRef}
            type="text"
            placeholder="Search for food"
            aria-label="Search for food"
            maxLength={MAX_QUERY_LENGTH}
            enterKeyHint="search"
            value={searchString}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
          />
          <button
            type="button"
            aria-label="Search"
            className={styles.searchBtn}
            onClick={() => submit(searchString)}
          >
            <SearchIcon fill="#ffffff" />
          </button>
          {searchString.length > 0 ? (
            <Container style={{ padding: '0' }}>
              <div className={styles.suggestions}>
                {suggestions.map((e, i) => (
                  <Fragment key={`${e.food_id}-${i}`}>
                    <FoodListElement
                      food={e}
                      href={foodHref(e)}
                    />
                    <hr className={styles.elementDivider} />
                  </Fragment>
                ))}
              </div>
            </Container>
          ) : null}
        </div>
      )}
    </div>
  );
};
